import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import GroupsIcon from "@mui/icons-material/Groups";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import StorageIcon from "@mui/icons-material/Storage";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";

import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import GlobalStyles from "@mui/material/GlobalStyles";
import PenPaperIcon from "../../components/icons/PenPaperIcon";

import {
  fetchCostAnalysis,
  submitCostAnalysis,
  addLineItem,
  updateLineItem,
  deleteLineItem,
  requestCostAnalysisReopen,
} from "./types/cost-analysis-api";
import type {
  CategoryType,
  CostAnalysisDetail,
  CostCategory,
  CostLineItem,
  SubmitTarget,
} from "./types/cost-analysis";
import { usePrintPortal } from "./types/use-print-portal";
import CrewPaymentModal from "./CrewPaymentModal";
import { toast } from "react-toastify";

// NOTE: `CostLineItem` needs a `detail?: string | null` field added in
// ./types/cost-analysis.ts (subtitle text like "4 crew members × $2.00"),
// matching the new `detail` column on the backend model/serializer.

const CATEGORY_ICON: Record<CategoryType, ReactNode> = {
  travel: <LocalShippingOutlinedIcon fontSize="small" />,
  crew: <GroupsIcon fontSize="small" />,
  catering: <RestaurantIcon fontSize="small" />,
  data: <StorageIcon fontSize="small" />,
  memory_table: <ImageOutlinedIcon fontSize="small" />,
  other: <MoreHorizIcon fontSize="small" />,
};

const apiErrorMessage = (error: unknown, fallback: string) =>
  (error as { response?: { data?: { detail?: string } } })?.response?.data
    ?.detail || (error as Error)?.message || fallback;

const SUBMIT_TARGETS: { value: SubmitTarget; label: string }[] = [
  { value: "finance", label: "Finance" },
];

// --- Print-to-one-page config -------------------------------------------
// Print shows categories collapsed (header + total only, no line items),
// which is normally short enough to fit one page on its own. This scaling
// hook is a safety net for edge cases — e.g. a case with a lot of
// categories — where even the collapsed rows overflow one page. It scales
// the whole printable area down (uniformly, x and y) via `transform:
// scale()`, a visual resize rather than a layout reflow, so nothing gets
// cut off or hidden — it just gets smaller.
//
// PRINT_PAGE_HEIGHT_PX is an approximation of the usable page height at
// 96 CSS px/in (the standard browser reference), for a Letter page with the
// @page margin set below. If your printer/paper is A4, or you change the
// @page margin, adjust this number (measure by printing a full page and
// comparing to a shorter one, or just nudge until it looks right).
const PRINT_PAGE_MARGIN_IN = 0.4;
const PRINT_PAGE_HEIGHT_IN = 11 - PRINT_PAGE_MARGIN_IN * 2;
const PRINT_PAGE_HEIGHT_PX = PRINT_PAGE_HEIGHT_IN * 96;
// Leave a little slack so we don't sit exactly on the edge of a page break.
const PRINT_SAFETY_MARGIN_PX = 24;

// The event's own date/time (Stream.date + Stream.start), for the small
// header line — not when the document happened to be viewed or printed.
// `dateStr` is "YYYY-MM-DD", `startTime` is "HH:MM:SS".
function formatEventDateTime(
  dateStr?: string | null,
  startTime?: string | null,
) {
  if (!dateStr) return null;
  // Append a time so this parses as local midnight rather than UTC, which
  // would otherwise roll the date back a day in western timezones.
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  if (!startTime) return date;
  const [hours, minutes] = startTime.split(":").map(Number);
  if (Number.isNaN(hours)) return date;
  const t = new Date();
  t.setHours(hours, minutes || 0, 0, 0);
  const time = t.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${date} · ${time}`;
}

function formatCurrency(value: string | number) {
  const n = typeof value === "string" ? parseFloat(value) : value;
  if (Number.isNaN(n)) return "$0.00";
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
}

function parseAmount(value: string) {
  const n = parseFloat(value);
  return Number.isNaN(n) ? null : n;
}

function usePrintFitToOnePage(elementId: string, deps: unknown[]) {
  useEffect(() => {
    const applyPrintLayout = () => {
      const el =
        document.querySelector<HTMLElement>(
          `#print-portal-root #${elementId}`,
        ) ?? document.getElementById(elementId);

      if (!el) return;

      // Reset anything from a previous print
      el.style.removeProperty("zoom");
      el.style.removeProperty("transform");
      el.style.removeProperty("transform-origin");
      el.style.removeProperty("width");
      el.style.removeProperty("max-width");

      // Let the print CSS control the page dimensions.
      el.style.setProperty("width", "100%", "important");
      el.style.setProperty("max-width", "none", "important");

      // Wait for the browser to finish applying print styles
      requestAnimationFrame(() => {
        const naturalHeight = el.scrollHeight;

        const availableHeight = PRINT_PAGE_HEIGHT_PX - PRINT_SAFETY_MARGIN_PX;

        // ONLY shrink if the content is actually too tall.
        if (naturalHeight > availableHeight) {
          const scale = Math.max(availableHeight / naturalHeight, 0.55);

          el.style.setProperty("zoom", String(scale));
        }
      });
    };

    const resetPrintLayout = () => {
      const el =
        document.querySelector<HTMLElement>(
          `#print-portal-root #${elementId}`,
        ) ?? document.getElementById(elementId);

      if (!el) return;

      el.style.removeProperty("zoom");
      el.style.removeProperty("transform");
      el.style.removeProperty("transform-origin");
      el.style.removeProperty("width");
      el.style.removeProperty("max-width");
    };

    window.addEventListener("beforeprint", applyPrintLayout);
    window.addEventListener("afterprint", resetPrintLayout);

    return () => {
      window.removeEventListener("beforeprint", applyPrintLayout);
      window.removeEventListener("afterprint", resetPrintLayout);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/** Inline label + amount editor, shared by "add item" and "edit item" flows. */
function LineItemForm({
  initialLabel = "",
  initialAmount = "",
  initialDetail,
  saving,
  confirmLabel,
  onCancel,
  onSave,
  labelLocked = false,
}: {
  initialLabel?: string;
  initialAmount?: string;
  saving: boolean;
  confirmLabel: string;
  onCancel: () => void;
  onSave: (label: string, amount: number, detail?: string) => void;
  labelLocked?: boolean;
  initialDetail?: string | null;
}) {
  const [label, setLabel] = useState(initialLabel);
  const [amount, setAmount] = useState(initialAmount);
  const [detail, setDetail] = useState(initialDetail ?? "");

  const parsed = parseAmount(amount);
  const canSave = label.trim().length > 0 && parsed !== null;

  return (
    <Stack
      spacing={1}
      onClick={(e) => e.stopPropagation()}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <TextField
        size="small"
        placeholder="Label"
        value={label}
        autoFocus={!labelLocked}
        disabled={labelLocked}
        onChange={(e) => setLabel(e.target.value)}
        sx={{ flex: 1 }}
      />
      <TextField
        size="small"
        placeholder="Amount"
        type="number"
        value={amount}
        autoFocus={labelLocked}
        onChange={(e) => setAmount(e.target.value)}
        sx={{ width: 120 }}
      />
      <Tooltip title={confirmLabel}>
        <span>
          <IconButton
            size="small"
            color="primary"
            disabled={!canSave || saving}
            onClick={() =>
              canSave && onSave(label.trim(), parsed as number, detail.trim())
            }
          >
            <CheckIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <IconButton size="small" onClick={onCancel} disabled={saving}>
        <CloseIcon fontSize="small" />
      </IconButton>
      </Stack>
      {initialDetail !== undefined && (
        <TextField
          size="small"
          fullWidth
          label="Description"
          placeholder="Add a short explanation for this cost"
          value={detail}
          disabled={saving}
          onChange={(event) => setDetail(event.target.value)}
        />
      )}
    </Stack>
  );
}

/** A single line item row: view state, or swapped for an inline edit / delete-confirm state.
 *  Edit/delete actions reveal on hover — no separate "editing mode" needed.
 *  View state shows `label` as the heading and, when present, `detail` as
 *  a muted subtitle underneath (e.g. "4 crew members × $2.00") instead of
 *  the old dash-separated label + "auto" badge. */
function LineItemRow({
  item,
  onUpdate,
  onDelete,
  editable,
}: {
  item: CostLineItem;
  onUpdate: (label: string, amount: number, detail?: string) => Promise<void>;
  onDelete: () => Promise<void>;
  editable: boolean;
}) {
  const [mode, setMode] = useState<"view" | "edit" | "confirm-delete">("view");
  const [busy, setBusy] = useState(false);
  const [hovered, setHovered] = useState(false);

  const handleSave = async (label: string, amount: number, detail?: string) => {
    setBusy(true);
    try {
      // Locked items keep their existing label regardless of form state.
      await onUpdate(item.is_locked ? item.label : label, amount, detail);
      setMode("view");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await onDelete();
    } finally {
      setBusy(false);
    }
  };

  if (mode === "edit") {
    return (
      <LineItemForm
        initialLabel={item.label}
        initialAmount={String(item.amount)}
        initialDetail={item.detail}
        saving={busy}
        confirmLabel="Save changes"
        onCancel={() => setMode("view")}
        onSave={handleSave}
        labelLocked={item.is_locked}
      />
    );
  }

  if (mode === "confirm-delete") {
    return (
      <Stack
        direction="row"
        spacing={1.5}
        onClick={(e) => e.stopPropagation()}
        sx={{ alignItems: "center" }}
      >
        <Typography variant="body2" sx={{ color: "text.secondary", flex: 1 }}>
          Delete {item.label}?
        </Typography>
        <Button
          size="small"
          color="error"
          variant="contained"
          disableElevation
          disabled={busy}
          onClick={handleDelete}
          sx={{ textTransform: "none" }}
        >
          {busy ? "Deleting…" : "Delete"}
        </Button>
        <Button
          size="small"
          disabled={busy}
          onClick={() => setMode("view")}
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
      </Stack>
    );
  }

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 1,
        minHeight: 32,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ color: "text.primary", fontWeight: 500, fontSize: 13 }}
        >
          {item.label}
        </Typography>
        {item.detail && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: 12,
              display: "block",
              mt: 0.25,
              textTransform: item.role ? "capitalize" : "none",
            }}
          >
            {item.detail}
          </Typography>
        )}
      </Box>

      <Stack
        direction="row"
        spacing={0.5}
        sx={{ alignItems: "center", flexShrink: 0 }}
      >
        <Typography
          variant="body2"
          sx={{ color: "text.primary", fontWeight: 700 }}
        >
          {formatCurrency(item.amount)}
        </Typography>
        {editable && <Stack
          direction="row"
          onClick={(e) => e.stopPropagation()}
          className="print-hide"
          sx={{
            opacity: hovered ? 1 : 0,
            transition: "opacity 100ms ease",
            pointerEvents: hovered ? "auto" : "none",
          }}
        >
          <IconButton size="small" onClick={() => setMode("edit")}>
            <PenPaperIcon sx={{ fontSize: 16 }} />
          </IconButton>
          {/* Locked (fixed-slot) items can't be deleted */}
          {!item.is_locked && (
            <IconButton size="small" onClick={() => setMode("confirm-delete")}>
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Stack>}
      </Stack>
    </Box>
  );
}

function ApprovedStamp() {
  return (
    <Box
      sx={{
        position: "absolute",
        top: 24,
        right: 24,
        border: "3px solid #4C7A3D",
        borderRadius: 2,
        px: 3,
        py: 1,
        transform: "rotate(-8deg)",
        opacity: 0.85,
        pointerEvents: "none",
        userSelect: "none",
        zIndex: 1,
      }}
    >
      <Typography
        sx={{
          fontSize: 22,
          fontWeight: 900,
          letterSpacing: "0.15em",
          color: "#4C7A3D",
        }}
      >
        APPROVED
      </Typography>
    </Box>
  );
}
function SignatureLine({ label }: { label: string }) {
  return (
    <Box sx={{ flex: 1 }}>
      <Box
        sx={{
          borderBottom: "1px solid #3B2E27",
          height: 48,

          "@media print": {
            height: "28px",
          },
        }}
      />
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", mt: 0.5, display: "block" }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function CategoryRow({
  category,
  onAddLineItem,
  onUpdateLineItem,
  onDeleteLineItem,
  onOpenCrewPayment,
  editable,
}: {
  category: CostCategory;
  onAddLineItem: (
    categoryId: number,
    label: string,
    amount: number,
  ) => Promise<void>;
  onUpdateLineItem: (
    categoryId: number,
    itemId: number,
    label: string,
    amount: number,
    _detail?: string,
  ) => Promise<void>;
  onDeleteLineItem: (categoryId: number, itemId: number) => Promise<void>;
  onOpenCrewPayment?: () => void;
  editable: boolean;
}) {
  const [open, setOpen] = useState(true);
  const [addingItem, setAddingItem] = useState(false);
  const [addSaving, setAddSaving] = useState(false);

  const hasItems = category.line_items.length > 0;

  // Flag when the line items don't add up to the category's stated total —
  // a quick way to catch stale or manually-overridden amounts.
  const computedTotal = category.line_items.reduce(
    (sum, i) => sum + Number(i.amount || 0),
    0,
  );
  const statedTotal = Number(category.amount || 0);
  const isMismatched = hasItems && Math.abs(computedTotal - statedTotal) > 0.01;

  const handleAdd = async (label: string, amount: number) => {
    setAddSaving(true);
    try {
      await onAddLineItem(category.id, label, amount);
      setAddingItem(false);
      setOpen(true);
    } finally {
      setAddSaving(false);
    }
  };

  return (
    <Paper
      elevation={0}
      variant="outlined"
      className="print-category"
      sx={{
        borderColor: open ? "#D9CFC7" : "divider",
        borderRadius: 3,
        overflow: "hidden",
        transition: "border-color 120ms ease",
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 2.5,
          py: 2,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <Box
          className="print-compact-icon"
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            bgcolor: open ? "primary.main" : "#ECE7E2",
            color: open ? "primary.contrastText" : "#6E645D",
          }}
        >
          {CATEGORY_ICON[category.category_type]}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{ fontWeight: 700, color: "text.primary", lineHeight: 1.3 }}
          >
            {category.category_type_display}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontWeight: 500 }}
          >
            {hasItems
              ? `${category.line_items.length} item${category.line_items.length === 1 ? "" : "s"}`
              : "No items yet"}
          </Typography>
        </Box>

        {isMismatched && (
          <Tooltip
            title={`Line items total ${formatCurrency(computedTotal)}, category total is ${formatCurrency(statedTotal)}`}
          >
            <WarningAmberIcon sx={{ fontSize: 18, color: "warning.main" }} />
          </Tooltip>
        )}

        <Typography
          sx={{ fontWeight: 700, color: "text.primary", whiteSpace: "nowrap" }}
        >
          {formatCurrency(category.amount)}
        </Typography>

        <IconButton
          size="small"
          disableRipple
          className="print-hide"
          sx={{ color: "text.secondary", ml: 0.5, pointerEvents: "none" }}
          aria-hidden
        >
          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Box>

      {/* Collapsed by default in print — only the category header (name,
          item count, total) shows up on the printout. The full line-item
          breakdown stays screen-only. */}
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Divider className="print-hide" sx={{ borderColor: "divider" }} />
        <Box className="print-hide" sx={{ px: 2.5, py: 2 }}>
          <Stack
            spacing={1.25}
            divider={<Divider flexItem sx={{ borderColor: "#EFEAE5" }} />}
          >
            {category.category_type === "data" && category.calculation && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  borderRadius: 2,
                  bgcolor: "#F8F6F4",
                  border: "1px solid #EAE1D8",
                  overflow: "hidden",
                }}
              >
                {[
                  ["Duration", `${Number(category.calculation.duration_hours)} hours`],
                  ["Bitrate", `${Number(category.calculation.bitrate_mbps)} Mbps`],
                  ["Estimated use", `${Number(category.calculation.estimated_usage_gb)} GB`],
                ].map(([label, value], index) => (
                  <Box
                    key={label}
                    sx={{
                      px: 1.5,
                      py: 1.25,
                      borderLeft: index ? "1px solid #EAE1D8" : "none",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            {category.line_items.map((item) => (
              <LineItemRow
                key={item.id}
                item={item}
                onUpdate={(label, amount, detail) =>
                  onUpdateLineItem(category.id, item.id, label, amount, detail)
                }
                onDelete={() => onDeleteLineItem(category.id, item.id)}
                editable={editable}
              />
            ))}

            {editable && category.category_type === "data" && (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Use the pencil beside a provider to adjust its price.
              </Typography>
            )}

            {!hasItems && !addingItem && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontStyle: "italic" }}
              >
                Nothing here yet.
              </Typography>
            )}

            {editable && category.category_type === "other" && !addingItem && (
              <Button
                size="small"
                startIcon={<AddIcon fontSize="small" />}
                onClick={(e) => {
                  e.stopPropagation();
                  setAddingItem(true);
                }}
                className="print-hide"
                sx={{
                  alignSelf: "flex-start",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Add line item
              </Button>
            )}

            {category.category_type === "other" && addingItem && (
              <LineItemForm
                saving={addSaving}
                confirmLabel="Add item"
                onCancel={() => setAddingItem(false)}
                onSave={handleAdd}
              />
            )}

            {editable && category.category_type === "crew" && onOpenCrewPayment && (
              <Button
                size="small"
                startIcon={<AddIcon fontSize="small" />}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenCrewPayment();
                }}
                className="print-hide"
                sx={{
                  alignSelf: "flex-start",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Pay crew member
              </Button>
            )}
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
}

function InfoCard({
  media,
  eyebrow,
  title,
  detailIcon,
  detail,
}: {
  media: ReactNode;
  eyebrow: string;
  title: string;
  detailIcon: ReactNode;
  detail: string;
}) {
  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        flex: 1,
        borderColor: "divider",
        borderRadius: 3,
        p: 2,
        display: "flex",
        gap: 2,
        alignItems: "center",

        "@media print": {
          minWidth: 0,
          height: "68px",
          padding: "10px 14px !important",
          borderRadius: "8px",
          gap: "10px",
          boxSizing: "border-box",
        },
      }}
    >
      {media}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 11,
            letterSpacing: "0.08em",
            fontWeight: 700,
            color: "text.secondary",
          }}
        >
          {eyebrow}
        </Typography>
        <Typography
          sx={{ fontWeight: 700, color: "text.primary", mt: 0.25 }}
          noWrap
        >
          {title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mt: 0.5,
            color: "text.secondary",
          }}
        >
          {detailIcon}
          <Typography variant="body2" sx={{ color: "text.primary" }} noWrap>
            {detail}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

export default function CostAnalysis({
  id: idProp,
  autoPrint = false,
  onAfterPrint,
}: {
  id?: string | number;
  // When true, opens the print dialog for this document as soon as it has
  // loaded — used by other views (e.g. Finance) that want to trigger a real
  // print of the actual cost analysis without embedding this whole page.
  autoPrint?: boolean;
  onAfterPrint?: () => void;
}) {
  const params = useParams<{ id?: string }>();
  const id = idProp ?? params?.id;

  const [data, setData] = useState<CostAnalysisDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const triggerPrint = usePrintPortal("cost-analysis-print-area");
  const [sendAnchor, setSendAnchor] = useState<HTMLElement | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [crewPaymentOpen, setCrewPaymentOpen] = useState(false);
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const autoPrintedRef = useRef(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    fetchCostAnalysis(id)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            apiErrorMessage(
              err,
              "Crew has not been assigned to this stream yet, so its cost analysis is not available.",
            ),
          );
          // Loading failed, so the print-triggering effect below will never
          // get data to fire on — let the caller know printing won't happen.
          if (autoPrint) onAfterPrint?.();
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Scale the printable area down to fit one page right before printing,
  // and restore it afterwards. Re-measures whenever the underlying data
  // changes so it stays accurate as items are added/removed/edited.
  usePrintFitToOnePage("cost-analysis-print-area", [data]);

  // Fires the browser print dialog once, right after the document has
  // loaded, when this instance was mounted purely to print (see `autoPrint`
  // above). Guarded so it can't re-fire on later re-renders.
  useEffect(() => {
    if (!autoPrint || !data || autoPrintedRef.current) return;
    autoPrintedRef.current = true;

    const raf = requestAnimationFrame(() => triggerPrint());
    const handleAfterPrint = () => onAfterPrint?.();
    window.addEventListener("afterprint", handleAfterPrint);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("afterprint", handleAfterPrint);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPrint, data]);

  const venue = data?.venue;
  const eventDateTime = formatEventDateTime(
    data?.stream?.date,
    data?.stream?.start,
  );
  // Prefer the deceased's photo for the Client Record card; fall back to
  // the stream's own thumbnail image if no deceased photo was uploaded.
  const clientRecordImage =
    data?.stream?.deceased_image || data?.stream?.stream_thumbnail_background_image || null;

  const handleSend = async (target: SubmitTarget) => {
    if (!data) return;
    setSendAnchor(null);
    setSubmitting(true);
    try {
      const updated = await submitCostAnalysis(data.id, target);
      setData(updated);
      toast.success(
        `Sent to ${SUBMIT_TARGETS.find((t) => t.value === target)?.label}`,
      );
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to send"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddLineItem = async (
    categoryId: number,
    label: string,
    amount: number,
  ) => {
    if (!data) return;
    try {
      const updated = await addLineItem(data.id, {
        category_id: categoryId,
        label,
        amount,
      });
      setData(updated);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to add line item"));
      throw err;
    }
  };

  const handleUpdateLineItem = async (
    categoryId: number,
    itemId: number,
    label: string,
    amount: number,
    detail?: string,
  ) => {
    if (!data) return;
    try {
      const updated = await updateLineItem(data.id, itemId, {
        label,
        amount,
        detail: detail || null,
      });
      setData(updated);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to update line item"));
      throw err;
    }
  };

  const handleDeleteLineItem = async (categoryId: number, itemId: number) => {
    if (!data) return;
    try {
      const updated = await deleteLineItem(data.id, itemId);
      setData(updated);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to delete line item"));
      throw err;
    }
  };

  const handleReopen = async () => {
    if (!data) return;
    const reason = data.status === "approved" ? reopenReason.trim() : "";
    if (data.status === "approved" && !reason) return;
    setSubmitting(true);
    try {
      const updated = await requestCostAnalysisReopen(data.id, reason);
      setData(updated);
      setReopenDialogOpen(false);
      setReopenReason("");
      toast.success(
        updated.status === "reopen_requested"
          ? "Reopening request sent to Finance."
          : "Cost analysis returned to draft.",
      );
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to reopen"));
    } finally {
      setSubmitting(false);
    }
  };

  const editable = data?.status === "draft";

  const sendDisabled = useMemo(
    () => submitting || data?.status !== "draft",
    [submitting, data?.status],
  );

  const computedGrandTotal =
    data?.categories.reduce((sum, c) => sum + Number(c.amount || 0), 0) ?? 0;
  const grandTotalMismatched =
    !!data &&
    Math.abs(computedGrandTotal - Number(data.total_amount || 0)) > 0.01;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ maxWidth: 640, mx: "auto", py: 10 }}>
        <Alert severity="info">
          {error ??
            "Crew has not been assigned to this stream yet. The cost analysis will become available after the first crew assignment."}
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <GlobalStyles
        styles={{
          "@media print": {
            "@page": {
              margin: "0.4in",
              size: "A4 portrait",
            },

            html: {
              WebkitPrintColorAdjust: "exact",
              printColorAdjust: "exact",
            },

            body: {
              margin: 0,
              padding: 0,
            },

            "#cost-analysis-print-area": {
              width: "100% !important",
              maxWidth: "none !important",
              minHeight: "calc(297mm - 20.32mm) !important",
              boxSizing: "border-box",
              padding: "18mm 14mm !important",
              margin: "0 !important",
              display: "flex !important",
              flexDirection: "column !important",
            },

            ".print-hide": {
              display: "none !important",
            },

            ".print-only": {
              display: "block !important",
            },

            // Make the printed cards more spacious now that the layout
            // fills the page intentionally instead of huddling at the top.
            ".print-category > div:first-child": {
              minHeight: "64px !important",
              padding: "14px 18px !important",
            },

            ".print-compact-icon": {
              width: "38px !important",
              height: "38px !important",
            },
            ".print-info-cards": {
              flexDirection: "row !important",
              gap: "12px !important",
            },

            ".print-venue-card .MuiPaper-root": {
              height: "84px !important",
              padding: "14px 18px !important",
            },

            // A bit more breathing room between collapsed category cards.
            ".print-categories > .print-category + .print-category": {
              marginTop: "14px !important",
            },

            ".print-signatures": {
              // Pushed to the bottom of the flex column instead of a fixed
              // margin, so short breakdowns don't leave a dead gap above it.
              marginTop: "auto !important",
              paddingTop: "10px !important",
            },

            ".print-category": {
              breakInside: "avoid",
              pageBreakInside: "avoid",
              marginBottom: "0 !important",
            },
            // Prevent the page from looking compressed vertically
            "#cost-analysis-print-area .MuiStack-root": {
              boxSizing: "border-box",
            },
          },
        }}
      />
      <Box
        id="cost-analysis-print-area"
        sx={{
          width: "100%",
          maxWidth: 1240,
          mx: "auto",
          px: { xs: 2, md: 4 },
          py: { xs: 3, md: 5 },
          position: "relative",

          "@media print": {
            width: "100% !important",
            maxWidth: "none !important",
            margin: "0 !important",
            padding: "18mm 14mm !important",
            minHeight: "calc(297mm - 20.32mm)",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {data.status === "approved" && <ApprovedStamp />}
        {/* Header */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ justifyContent: "space-between", alignItems: "flex-start" }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 13,
                letterSpacing: "0.06em",
                fontWeight: 700,
                color: "text.secondary",
              }}
            >
              COST ANALYSIS
            </Typography>
            <Typography
              sx={{
                fontSize: 20,
                color: "text.primary",
                fontWeight: 700,
                mt: 0.5,
              }}
            >
              {data.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.25 }}
            >
              Case #{data.case_number}
            </Typography>
            {eventDateTime && (
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block", mt: 0.5 }}
              >
                Event date: {eventDateTime}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              bgcolor: "#E7D8CF",
              borderRadius: 2.5,
              px: 4,
              py: 2,
              textAlign: "center",
              minWidth: 220,
            }}
          >
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ justifyContent: "center", alignItems: "center" }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  letterSpacing: "0.1em",
                  fontWeight: 700,
                  color: "#7C6A60",
                }}
              >
                TOTAL ESTIMATED COST
              </Typography>
              {grandTotalMismatched && (
                <Tooltip
                  title={`Categories add up to ${formatCurrency(computedGrandTotal)}`}
                >
                  <WarningAmberIcon
                    sx={{ fontSize: 14, color: "warning.dark" }}
                  />
                </Tooltip>
              )}
            </Stack>
            <Typography
              sx={{ fontSize: 26, fontWeight: 700, color: "#3B2E27", mt: 0.5 }}
            >
              {formatCurrency(data.total_amount)}
            </Typography>
          </Box>
        </Stack>

        {/* Info cards */}
        {/* Info cards — Client Record is now print-hidden; Primary Venue
    expands to fill the full row on the printout instead of splitting 50/50. */}
        <Stack
          className="print-info-cards"
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          sx={{
            mt: 4,

            "@media print": {
              flexDirection: "row !important",
              gap: "12px !important",
              marginTop: "18px !important",
            },
          }}
        >
          <Box className="print-venue-card" sx={{ flex: 1, minWidth: 0 }}>
            <InfoCard
              media={
                venue?.cover_photo ? (
                  <Box
                    component="img"
                    src={venue.cover_photo}
                    alt={venue?.name ?? "Venue"}
                    sx={{
                      width: 116,
                      height: 92,
                      borderRadius: 2,
                      objectFit: "cover",
                      flexShrink: 0,
                      "@media print": {
                        width: "72px",
                        height: "72px",
                        borderRadius: "8px",
                      },
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 116,
                      height: 92,
                      borderRadius: 2,
                      bgcolor: "#E5E5E5",
                      flexShrink: 0,
                    }}
                  />
                )
              }
              eyebrow="PRIMARY VENUE"
              title={venue?.name ?? "No venue linked"}
              detailIcon={<PlaceOutlinedIcon sx={{ fontSize: 16 }} />}
              detail={venue?.address ?? "—"}
            />
          </Box>

          {/* Client Record card — screen only, hidden in print */}
          <Box className="print-hide" sx={{ flex: 1, minWidth: 0 }}>
            <InfoCard
              media={
                clientRecordImage ? (
                  <Box
                    component="img"
                    src={clientRecordImage}
                    alt={data.stream?.deceased_name ?? data.title}
                    sx={{
                      width: 92,
                      height: 92,
                      borderRadius: 2,
                      objectFit: "cover",
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 92,
                      height: 92,
                      borderRadius: 2,
                      bgcolor: "#E5E5E5",
                      flexShrink: 0,
                    }}
                  />
                )
              }
              eyebrow="CLIENT RECORD"
              title={data.title}
              detailIcon={<DescriptionOutlinedIcon sx={{ fontSize: 16 }} />}
              detail={data.client_ref ? `Billing Ref: #${data.client_ref}` : ""}
            />
          </Box>
        </Stack>

        {/* Breakdown */}
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "baseline",
            mt: 5,
            mb: 2,
          }}
        >
          <Typography
            sx={{ fontSize: 18, color: "text.primary", fontWeight: 700 }}
          >
            Breakdown Categories
          </Typography>
        </Stack>

        {/* flex:1 in print lets this section fill the remaining page
            height, so the signature block anchors to the bottom instead
            of leaving a gap under a short category list. */}
        <Stack
          className="print-categories"
          spacing={2}
          sx={{ "@media print": { flex: 1 } }}
        >
          {data.categories.map((category) => (
            <CategoryRow
              key={category.id}
              category={category}
              onAddLineItem={handleAddLineItem}
              onUpdateLineItem={handleUpdateLineItem}
              onDeleteLineItem={handleDeleteLineItem}
              onOpenCrewPayment={
                category.category_type === "crew"
                  ? () => setCrewPaymentOpen(true)
                  : undefined
              }
              editable={editable}
            />
          ))}
        </Stack>

        {/* Totals recap — print-only. Gives the collapsed category list a
            proper closing line before the signature block, the way a
            finance document normally ends its breakdown. */}
        <Box
          className="print-hide print-only"
          sx={{
            display: "none",
            "@media print": {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              mt: 2,
              pt: 1.5,
              borderTop: "2px solid #3B2E27",
            },
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
            Total Estimated Cost
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
            {formatCurrency(data.total_amount)}
          </Typography>
        </Box>

        {/* Signature section — for manual sign-off after printing. Two rows
            so Producer Signature/Name can follow Finance Signature without
            crowding a single row on narrower print widths. */}
        <Stack
          className="print-signatures"
          spacing={2.5}
          sx={{
            mt: 5,
            pt: 3,
            borderTop: "1px solid",
            borderColor: "divider",

            "@media print": {
              marginTop: "auto !important",
              paddingTop: "10px !important",
              gap: "12px !important",
            },
          }}
        >
          <Stack
            direction="row"
            spacing={4}
            sx={{ "@media print": { gap: "40px !important" } }}
          >
            <Stack spacing={2.5} sx={{ flex: 1 }}>
              <SignatureLine label="Finance Signature" />
            </Stack>
            <Stack spacing={2.5} sx={{ flex: 1 }}>
              <SignatureLine label="Producer Signature" />
            </Stack>
          </Stack>
          <Stack
            direction="row"
            spacing={4}
            sx={{ "@media print": { gap: "40px !important" } }}
          >
            <Stack spacing={2.5} sx={{ flex: 1 }}>
              <SignatureLine label="Producer Name" />
            </Stack>
            <Stack spacing={2.5} sx={{ flex: 1 }}>
              <SignatureLine label="Date" />
            </Stack>
          </Stack>
        </Stack>

        {/* Print-only footer, small and muted, so the page has a
            deliberate bottom edge rather than trailing off. */}
        <Typography
          className="print-hide print-only"
          sx={{
            display: "none",
            "@media print": {
              display: "block",
              fontSize: 9,
              color: "text.secondary",
              mt: 1.5,
            },
          }}
        >
          Generated {new Date().toLocaleString()}
        </Typography>

        {/* Footer */}
        <Stack
          direction="row"
          sx={{
            mt: 5,
            flexWrap: "wrap",
            gap: 2,
            justifyContent: "space-between",
            alignItems: "center",
            "@media print": { display: "none" },
          }}
        >
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              startIcon={<PrintOutlinedIcon />}
              onClick={triggerPrint}
              sx={{
                textTransform: "none",
                color: "text.primary",
                borderColor: "#D9D2CC",
                bgcolor: "background.paper",
                px: 2.5,
                py: 1.1,
                fontWeight: 600,
                "&:hover": {
                  borderColor: "#C3B9B0",
                  bgcolor: "background.paper",
                },
              }}
            >
              Print
            </Button>
          </Stack>

          <Stack direction="row" spacing={1.5}>
            {(data.status === "awaiting_audit" || data.status === "approved" || data.status === "rejected") && (
              <Button
                variant="outlined"
                disabled={submitting}
                onClick={() => {
                  if (data.status === "approved") {
                    setReopenDialogOpen(true);
                  } else {
                    void handleReopen();
                  }
                }}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                {data.status === "awaiting_audit"
                  ? "Withdraw submission"
                  : data.status === "approved"
                    ? "Request reopening"
                    : "Reopen and edit"}
              </Button>
            )}
            {data.status === "reopen_requested" && (
              <Button variant="outlined" disabled sx={{ textTransform: "none", fontWeight: 700 }}>
                Reopening requested
              </Button>
            )}
          <Box sx={{ display: "flex" }}>
            <Button
              variant="contained"
              disableElevation
              disabled={sendDisabled}
              onClick={(e) => setSendAnchor(e.currentTarget)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                px: 4,
                py: 1.25,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
              }}
            >
              {data.status === "awaiting_audit"
                ? `Sent to ${data.submitted_to}`
                : "Send"}
            </Button>
            <Button
              variant="contained"
              disableElevation
              disabled={sendDisabled}
              aria-label="Send options"
              onClick={(e) => setSendAnchor(e.currentTarget)}
              sx={{
                minWidth: 44,
                px: 0,
                borderLeft: "1px solid rgba(255,255,255,0.18)",
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
              }}
            >
              <ArrowDropDownIcon />
            </Button>
          </Box>
          </Stack>

          <Menu
            anchorEl={sendAnchor}
            open={Boolean(sendAnchor)}
            onClose={() => setSendAnchor(null)}
          >
            {SUBMIT_TARGETS.map((t) => (
              <MenuItem key={t.value} onClick={() => handleSend(t.value)}>
                {t.label}
              </MenuItem>
            ))}
          </Menu>
        </Stack>

        <Dialog
          open={reopenDialogOpen}
          onClose={() => !submitting && setReopenDialogOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Request reopening</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              Explain what needs to change. The analysis will remain approved and locked until Finance accepts this request.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={3}
              label="Reason for reopening"
              value={reopenReason}
              onChange={(event) => setReopenReason(event.target.value)}
              disabled={submitting}
            />
          </DialogContent>
          <DialogActions>
            <Button disabled={submitting} onClick={() => setReopenDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              disabled={submitting || !reopenReason.trim()}
              onClick={() => void handleReopen()}
            >
              {submitting ? "Sending…" : "Send request"}
            </Button>
          </DialogActions>
        </Dialog>
        <CrewPaymentModal
          open={crewPaymentOpen}
          onClose={() => setCrewPaymentOpen(false)}
          costAnalysisId={data.id}
          streamId={data.stream.id}
          onPaid={(updated) => setData(updated)}
        />
      </Box>
    </>
  );
}
